import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import {
  Button,
  Modal,
  Text,
  Stack,
  ButtonGroup,
  Shelf,
  Select,
  Input,
  Box,
} from '@chaser/components';
import { Rocket, CheckCircle2, User, Building2, MapPin, Building2Icon } from 'lucide-react';
import LabeledFieldWithError from '../LabeledFieldWithError';
import FieldWithError from '../FieldWithError';
import AccountService from '../../services/account-service';
import ConnectService from '../../services/connect-service';
import useUser from '../../hooks/useUser';
import useShortCode from '../../hooks/useShortCode';
import useOrganisation from '../../hooks/useOrganisation';
import useUserOrganisations from '../../hooks/useUserOrganisations';
import { useCacheActions } from '../../hooks/useFetch';
import countries from '../../app/addorganisation/welcome/countries';
import defaultCurrencies from '../../app/addorganisation/welcome/currencies';
import timezones from '../../util/timezones';
import styles from './OnboardingSurveyModal.module.scss';
import { PHONE_COUNTRY_CODES, ACCOUNTING_SOFTWARE_OPTIONS } from '../../util/constants';

const TEMPORARY_ORG_NAME = 'Organization Name';

const ACCOUNTING_FIRM_OPTIONS = [
  { label: 'Please select', value: '' },
  { label: 'Yes', value: 'yes' },
  { label: 'No', value: 'no' },
];

const baseValidationSchema = {
  businessName: Yup.string().required('Business name is required'),
  country: Yup.string().required('Country is required'),
  timezone: Yup.string().required('Timezone is required'),
  currency: Yup.string().required('Currency is required'),
};

const hubspotValidationSchema = {
  firstName: Yup.string().required('First name is required'),
  lastName: Yup.string().required('Last name is required'),
  phoneNumber: Yup.string()
    .required('Phone number is required')
    .matches(/^[\d\s\-().]{6,15}$/, 'Enter a valid phone number'),
  isAccountingFirm: Yup.string().required('Please select an option'),
  accountingSoftware: Yup.string().required('Please select an option'),
  howDidYouHear: Yup.string().required('Please tell us how you heard about Chaser'),
};

const SectionLabel = ({ icon: Icon, children }) => (
  <Shelf gap="xsmall" alignItems="center" className={styles.sectionLabel}>
    <Icon size={13} />
    <Text size="xsmall" color="Mid Neutral 1">
      {children}
    </Text>
  </Shelf>
);

const OnboardingSurveyModal = ({ open, close }) => {
  const user = useUser();
  const shortCode = useShortCode();
  const { invalidateDomain } = useCacheActions();
  const organisation = useOrganisation(shortCode, { suspense: false, throwOnError: false });
  const firstName = user?.profile?.firstName || user?.firstName || '';
  const lastName = user?.profile?.lastName || user?.lastName || '';

  const allOrganisations = useUserOrganisations();
  const isNewOrgFlow =
    Array.isArray(allOrganisations) &&
    (allOrganisations.length > 1 ||
      (allOrganisations.length >= 1 && !allOrganisations.some(o => o.ShortCode === shortCode)));
  const validationSchema = Yup.object({
    ...baseValidationSchema,
    ...(isNewOrgFlow ? {} : hubspotValidationSchema),
  });

  const orgName =
    organisation?.Name && organisation.Name !== TEMPORARY_ORG_NAME ? organisation.Name : '';

  return (
    <Formik
      initialValues={{
        firstName,
        lastName,
        phoneCountryCode: '+44',
        phoneNumber: '',
        businessName: orgName,
        country: organisation?.CountryCode || 'GB',
        timezone: organisation?.timezone || 'Europe/London',
        currency: organisation?.BaseCurrency || 'GBP',
        isAccountingFirm: '',
        accountingSoftware: '',
        howDidYouHear: '',
        _open: open,
      }}
      onSubmit={async (values, actions) => {
        try {
          if (!organisation?.hasConfirmedDetails) {
            await ConnectService.connectionSetup({
              shortCode,
              name: values.businessName.trim(),
              country: values.country,
              timezone: values.timezone,
              currency: values.currency,
            });
          }
          if (!isNewOrgFlow) {
            await AccountService.submitOnboardingSurvey({
              shortCode,
              firstName: values.firstName,
              lastName: values.lastName,
              phoneNumber: values.phoneNumber,
              phoneCountryCode: values.phoneCountryCode,
              isAccountingFirm: values.isAccountingFirm || undefined,
              accountingSoftware: values.accountingSoftware || undefined,
              howDidYouHear: values.howDidYouHear || undefined,
            });
          }
          invalidateDomain('organisation');
          invalidateDomain('user');
          close();
        } catch {
          // swallow — modal closes on unexpected error to not block user
          close();
        } finally {
          actions.setSubmitting(false);
        }
      }}
      validationSchema={validationSchema}
      enableReinitialize
    >
      {({ values, setFieldValue, isSubmitting }) => (
        <Modal
          open={open}
          toggle={undefined}
          disableClose
          size="large"
          body={
            <Form id="onboarding-survey-form" className={styles.modalBody}>
              <Stack gap="small">
                {isNewOrgFlow ? (
                  <Shelf gap="small" alignItems="center" className={styles.hero}>
                    <Box className={styles.heroIcon}>
                      <Building2Icon size={16} />
                    </Box>
                    <Stack gap="xsmall">
                      <span className={styles.trialBadge}>
                        <CheckCircle2 size={11} />
                        New organisation
                      </span>
                      <Text color="Dark Neutral 1">
                        <strong>Welcome to your new organisation!</strong>
                      </Text>
                      <Text size="small" color="Mid Neutral 1">
                        Confirm your organisation details to get set up.
                      </Text>
                    </Stack>
                  </Shelf>
                ) : (
                  <Shelf gap="small" alignItems="center" className={styles.hero}>
                    <Box className={styles.heroIcon}>
                      <Rocket size={16} />
                    </Box>
                    <Stack gap="xsmall">
                      <span className={styles.trialBadge}>
                        <CheckCircle2 size={11} />
                        Free trial active
                      </span>
                      <Text color="Dark Neutral 1">
                        <strong>
                          {firstName ? `Welcome, ${firstName}!` : 'Welcome to Chaser!'}
                        </strong>
                      </Text>
                      <Text size="small" color="Mid Neutral 1">
                        Help us personalise your account &mdash; it only takes a minute.
                      </Text>
                    </Stack>
                  </Shelf>
                )}

                {!isNewOrgFlow && (
                  <Stack gap="small">
                    <SectionLabel icon={User}>Your details</SectionLabel>
                    <Shelf gap="small" alignItems="flex-start">
                      <LabeledFieldWithError
                        style={{ flex: 1 }}
                        label="First name"
                        name="firstName"
                        required
                        as={Input}
                        placeholder="First name"
                      />
                      <LabeledFieldWithError
                        style={{ flex: 1 }}
                        label="Last name"
                        name="lastName"
                        required
                        as={Input}
                        placeholder="Last name"
                      />
                      <Stack gap="xsmall" style={{ flex: 1 }}>
                        <Text size="small" color="Mid Neutral 1">
                          Phone number
                        </Text>
                        <Shelf gap="small" alignItems="center">
                          <Select
                            value={values.phoneCountryCode}
                            onChange={e => setFieldValue('phoneCountryCode', e.target.value)}
                            className={styles.countryCodeSelect}
                          >
                            {PHONE_COUNTRY_CODES.map(opt => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </Select>
                          <FieldWithError
                            style={{ flex: 1 }}
                            name="phoneNumber"
                            as={Input}
                            placeholder="Your phone number"
                          />
                        </Shelf>
                      </Stack>
                    </Shelf>
                  </Stack>
                )}

                <Stack gap="small">
                  <SectionLabel icon={MapPin}>Your organisation</SectionLabel>
                  <Shelf gap="small">
                    <LabeledFieldWithError
                      style={{ flex: 1 }}
                      label="Business name"
                      name="businessName"
                      required
                      as={Input}
                      placeholder="Your company name"
                    />
                    <LabeledFieldWithError
                      style={{ flex: 1 }}
                      label="Country of business operation"
                      name="country"
                      required
                      as={Select}
                      className={styles.fullWidthSelect}
                    >
                      {countries.map(c => (
                        <option key={c.cca2} value={c.cca2}>
                          {c.name}
                        </option>
                      ))}
                    </LabeledFieldWithError>
                  </Shelf>
                  <Shelf gap="small">
                    <LabeledFieldWithError
                      style={{ flex: 1 }}
                      label="Timezone"
                      name="timezone"
                      required
                      as={Select}
                    >
                      {timezones.map(t => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </LabeledFieldWithError>
                    <LabeledFieldWithError
                      style={{ width: 160 }}
                      label="Currency"
                      name="currency"
                      required
                      as={Select}
                    >
                      {defaultCurrencies.map(c => (
                        <option key={c.code} value={c.code}>
                          {c.code}
                        </option>
                      ))}
                    </LabeledFieldWithError>
                  </Shelf>
                </Stack>

                {!isNewOrgFlow && (
                  <Stack gap="small">
                    <SectionLabel icon={Building2}>Your business</SectionLabel>
                    <Shelf gap="small">
                      <LabeledFieldWithError
                        style={{ flex: 1 }}
                        label="Do you work at an accounting or bookkeeping firm?"
                        name="isAccountingFirm"
                        required
                        as={Select}
                        className={styles.fullWidthSelect}
                      >
                        {ACCOUNTING_FIRM_OPTIONS.map(opt => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </LabeledFieldWithError>
                      <LabeledFieldWithError
                        style={{ flex: 1 }}
                        label="Which accounting software do you use?"
                        name="accountingSoftware"
                        required
                        as={Select}
                        className={styles.fullWidthSelect}
                      >
                        {ACCOUNTING_SOFTWARE_OPTIONS.map(opt => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </LabeledFieldWithError>
                    </Shelf>
                  </Stack>
                )}

                {!isNewOrgFlow && (
                  <LabeledFieldWithError
                    label="How did you hear about Chaser?"
                    name="howDidYouHear"
                    required
                    as={Input}
                    width="70%"
                    placeholder="e.g. Google, a colleague, social media…"
                  />
                )}
              </Stack>
            </Form>
          }
          footer={
            <Shelf justifyContent="space-between" width="100%">
              <Text size="xsmall" color="Mid Neutral 2">
                By submitting you agree to our{' '}
                <a
                  href="https://chaser.io/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.privacyLink}
                >
                  Privacy Policy
                </a>
                .
              </Text>
              <ButtonGroup>
                <Button type="submit" form="onboarding-survey-form" loading={isSubmitting}>
                  {isNewOrgFlow ? 'Confirm organisation details' : 'Get started'}
                </Button>
              </ButtonGroup>
            </Shelf>
          }
        />
      )}
    </Formik>
  );
};

export default OnboardingSurveyModal;
